import { useMemo, useState } from 'react';
import { COUNTRIES } from '../auth/constants';
import {
    PHONE_NUMBER_ERROR,
    applyNationalInput,
    countryCallingCode,
    describeStoredPhone,
    isPhoneNumberValid,
    isStoredPhoneUnparsed,
    resolvePhoneCountry
} from './phoneNumber';

export default function PhoneNumberInput({
    value,
    onChange,
    defaultCountry,
    placeholder = 'Mobile number',
    id = 'phone-number'
}) {
    const initial = describeStoredPhone(value, defaultCountry);
    const [country, setCountry] = useState(initial.country);
    const [text, setText] = useState(initial.text);
    const [dirty, setDirty] = useState(false);
    const [touched, setTouched] = useState(false);
    const [countryChosen, setCountryChosen] = useState(false);
    const [seenDefault, setSeenDefault] = useState(defaultCountry);

    if (defaultCountry !== seenDefault) {
        setSeenDefault(defaultCountry);
        const hasDigits = text.replace(/\D/g, '');
        const hasStoredValue = Boolean((value ?? '').trim());
        if (!countryChosen && !hasDigits && !hasStoredValue) {
            setCountry(resolvePhoneCountry(defaultCountry));
        }
    }

    const options = useMemo(() => {
        const listed = COUNTRIES.map(entry => ({
            code: entry.code,
            name: entry.name,
            callingCode: countryCallingCode(entry.code)
        }));
        if (country && !listed.some(entry => entry.code === country)) {
            const callingCode = countryCallingCode(country);
            if (callingCode) {
                listed.unshift({ code: country, name: country, callingCode });
            }
        }
        return listed;
    }, [country]);

    const trimmed = (value ?? '').trim();
    const showError = Boolean(trimmed)
        && !isPhoneNumberValid(trimmed)
        && (isStoredPhoneUnparsed(trimmed) || !dirty || touched);

    const commit = (next) => {
        setCountry(next.country);
        setText(next.text);
        onChange(next.e164);
    };

    const handleCountryChange = (event) => {
        const nextCountry = event.target.value;
        setCountryChosen(true);
        setDirty(true);
        const digits = text.replace(/\D/g, '');
        if (!digits && text.trim()) {
            setCountry(nextCountry);
            return;
        }
        commit(applyNationalInput(nextCountry, digits));
    };

    const handleNationalChange = (event) => {
        setDirty(true);
        commit(applyNationalInput(country, event.target.value, text));
    };

    return (
        <div className="phone-number-field">
            <select
                className="input-field phone-number-country"
                value={country}
                onChange={handleCountryChange}
                aria-label="Country calling code"
            >
                {options.map(entry => (
                    <option key={entry.code} value={entry.code}>
                        {entry.name} +{entry.callingCode}
                    </option>
                ))}
            </select>
            <input
                id={id}
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                className="input-field phone-number-national"
                placeholder={placeholder}
                value={text}
                onChange={handleNationalChange}
                onBlur={() => setTouched(true)}
                aria-invalid={showError}
                aria-describedby={showError ? `${id}-error` : undefined}
            />
            {showError && (
                <p id={`${id}-error`} className="phone-number-error">{PHONE_NUMBER_ERROR}</p>
            )}
        </div>
    );
}
